import { useInterval } from 'ahooks'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerBillInfo } from '@/api/type-server'
import { Loading } from '@/components/base/Loading'
import { EmptyView } from '@/components/EmptyView'
import { PurchasedEIP } from '@/models/eip'
import { formatDatePreset, formatDuration, formatMillicentPrice } from '@/utils/formatter'

interface BillingContentProps {
  eip: PurchasedEIP
}

export function BillingContent({ eip }: BillingContentProps) {
  const { t } = useTranslation('eip')
  const [bill, setBill] = useState<ServerBillInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const getData = useCallback(async () => {
    try {
      const res = await API.getEIPBill({ res_id: eip.resID })
      if (res.code === 0) {
        setBill(res.bill)
      }
    } finally {
      setLoading(false)
    }
  }, [eip.resID])

  useInterval(
    () => {
      getData()
    },
    60000,
    { immediate: true },
  )

  if (loading) {
    return (
      <div className="bg-panel-gray-100 rounded-lg p-4 flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!bill) {
    return (
      <div className="bg-panel-gray-100 rounded-lg p-4">
        <EmptyView content={t('detail.billingEmpty')} />
      </div>
    )
  }

  const startTime = bill.timeInfo?.startTime?.seconds ? bill.timeInfo.startTime.seconds * 1000 : 0
  const endTime = bill.timeInfo?.endTime?.seconds ? bill.timeInfo.endTime.seconds * 1000 : 0
  const isRunning = bill.type === 1
  const amount = Math.abs(bill.amount)
  const amountValue = formatMillicentPrice(amount * 1000, false, '') as string
  const amountCredits = Number(amountValue) > 1 ? 'Credits' : 'Credit'
  const actualEndTime = isRunning ? Date.now() : endTime
  const durationSec =
    startTime && actualEndTime ? Math.floor((actualEndTime - startTime) / 1000) : 0

  return (
    <div className="bg-panel-gray-100 rounded-lg flex flex-col py-4 t-sm text-tertiary gap-y-4">
      <div className="flex w-full justify-between">
        <div className="px-4">
          <div className="text-secondary">{t('detail.billingStartTime')}</div>
          <div className="text-primary">
            {startTime ? formatDatePreset(startTime, 'minute') : '-'}
          </div>
        </div>
        <div className="px-4">
          <div className="text-secondary">{t('detail.billingEndTime')}</div>
          <div className="text-primary">
            {isRunning ? (
              <div className="text-success">{t('detail.billingRunning')}</div>
            ) : endTime ? (
              formatDatePreset(endTime, 'minute')
            ) : (
              '-'
            )}
          </div>
        </div>
        <div className="px-4">
          <div className="text-secondary">{t('detail.billingPrice')}</div>
          <div className="text-primary">
            {bill.Info?.NetworkBillInfo?.priceMillicent
              ? (() => {
                  const priceValue = formatMillicentPrice(
                    bill.Info.NetworkBillInfo.priceMillicent,
                    false,
                    '',
                  ) as string
                  return (
                    <>
                      {priceValue}{' '}
                      <span className="text-tertiary">
                        {Number(priceValue) > 1 ? 'Credits' : 'Credit'}
                        {t('pricePerHour')}
                      </span>
                    </>
                  )
                })()
              : '-'}
          </div>
        </div>
        <div className="px-4">
          <div className="text-secondary">{t('detail.billingCost')}</div>
          <div className="text-primary">
            {amountValue} <span className="text-tertiary">{amountCredits}</span>
          </div>
        </div>
        <div className="px-4">
          <div className="text-secondary">{t('detail.billingDuration')}</div>
          <div className="text-primary">
            {durationSec > 0 ? formatDuration(durationSec, true) : '-'}
          </div>
        </div>
      </div>
    </div>
  )
}
